import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { useRoutePlanDelete, useRoutePlanEdit, useRoutePlanSetupDeliveries, useRoutes } from './apiHooks';
import Loader from '../../components/loader';
import RoutesMap from './routesMap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faTimes } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap/Modal';
import VehicleForm from './components/vehicleForm';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import DeliveryTable from './components/deliveryTable';
import RouteForm from './components/routeForm';
import { useQueryClient } from 'react-query';
import RouteTable from './components/routeTable';
import AdminRoutePlanStartDepot from './adminRoutePlanStartDepot';
import Helmet from 'react-helmet';

const AdminRoutePlanEdit = ({ routePlan, intl, selectedDeliveryDay, routePlanSetupId }) => {
  const [showOrderInfo, setShowOrderInfo] = useState(false);
  const [startDepot, setStartDepot] = useState(null);

  const {
    routes,
    isFetching: routesFetching,
    isLoading: routesLoading,
    isError: isRoutesError,
    error: routesError,
    refetch,
  } = useRoutes({
    routePlanId: routePlan.id,
    showOrderInfo,
  });

  const {
    deliveries: routePlanSetupDeliveries,
    isLoading: routePlanSetupDeliveriesLoading,
    isError: isRoutePlanSetupDeliveriesError,
    error: routePlanSetupDeliveriesError,
  } = useRoutePlanSetupDeliveries({
    routePlanSetupId,
    routesWithoutRoutePlan: true,
  });

  useEffect(() => {
    refetch();
  }, [showOrderInfo]);

  const {
    deleteRoutePlan,
    isLoading: deleteLoading,
    error: deleteError,
    isError: isDeleteError,
  } = useRoutePlanDelete();

  const {
    editRoutePlan,
    isLoading: editRoutePlanLoading,
    error: editRoutePlanError,
    isError: isEditRoutePlanError,
  } = useRoutePlanEdit();

  const [editVehicle, setEditVehicle] = useState(null);
  const [editRoute, setEditRoute] = useState(null);

  const deliveryIdsWithRoutes = routes
    ? routes.map((route) => route.deliveries.map((delivery) => delivery.id)).flat()
    : [];
  const routePlanDeliveriesWithoutRoute = routePlan.deliveries.filter(
    (delivery) => !deliveryIdsWithRoutes.some((d) => d === delivery.id)
  );

  const deliveriesWithoutRoute = routePlanSetupDeliveries
    ? routePlanSetupDeliveries.concat(routePlanDeliveriesWithoutRoute)
    : routePlanDeliveriesWithoutRoute;

  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  return (
    <div>
      <Loader show={routesLoading || routesFetching || routePlanSetupDeliveriesLoading} />
      <Helmet>
        <title>{formatMessage({ id: 'admin.route.planning.edit.header' }, { routePlanId: routePlan.id })}</title>
      </Helmet>
      <h3 className="m-b-3">
        <FormattedMessage id={'admin.route.planning.edit.header'} values={{ routePlanId: routePlan.id }} />
      </h3>

      <div className="m-b-1">
        {isEditRoutePlanError || isDeleteError ? (
          <ErrorMessage error={utils.getErrorMessageFromResponse(editRoutePlanError || deleteError)} />
        ) : (
          ''
        )}
      </div>
      <div className="m-b-1">
        {isRoutesError || isRoutePlanSetupDeliveriesError ? (
          <ErrorMessage error={utils.getErrorMessageFromResponse(routesError || routePlanSetupDeliveriesError)} />
        ) : (
          ''
        )}
      </div>

      <div className="m-b-3">
        <button
          onClick={() => {
            if (window.confirm(intl.formatMessage({ id: 'admin.route.planning.edit.actions.reoptimise.confirm' }))) {
              editRoutePlan({
                routePlan: { ...routePlan, startDepotId: startDepot?.id || routePlan.startDepot.id },
                params: { reoptimise: true },
              });
            }
          }}
          className={`btn btn-small btn-default m-r-2 ${editRoutePlanLoading ? 'btn-loading' : ''}`}
          disabled={editRoutePlanLoading}
        >
          <FontAwesomeIcon icon={faSync} /> <FormattedMessage id={'admin.route.planning.edit.actions.reoptimise'} />
        </button>
      </div>

      <div className="m-b-6">
        <AdminRoutePlanStartDepot setStartDepot={setStartDepot} startDepot={startDepot || routePlan.startDepot} />
      </div>

      <div className="m-b-6">
        <h3>
          <FormattedMessage id={'admin.route.planning.edit.vehicles.header'} />
        </h3>
        {routePlan.vehicles.map((vehicle) => {
          const actualCount = routes?.filter((route) => route.vehicle?.id === vehicle.id)?.length;

          return (
            <div className="m-b-2 form-block" key={vehicle.id}>
              <div className="text-underline">
                <strong>
                  {vehicle.vehicleId ? (
                    vehicle.vehicleId
                  ) : (
                    <FormattedMessage id={`admin.vehicle.types.${vehicle.vehicleType}`} />
                  )}
                </strong>
              </div>
              <div>
                <FormattedMessage
                  id={'admin.route.planning.edit.vehicles.actual.count'}
                  values={{ value: <strong>{actualCount}</strong> }}
                />
              </div>
              <div>
                <small>
                  <FormattedMessage
                    id={'admin.route.planning.edit.vehicles.capacity'}
                    values={{ value: vehicle.capacity }}
                  />
                </small>
              </div>
              <div>
                <small>
                  <FormattedMessage id={'admin.route.planning.edit.vehicles.count'} values={{ value: vehicle.count }} />
                </small>
              </div>
              {vehicle.maxTravelTimeInMinutes ? (
                <div>
                  <small>
                    <FormattedMessage
                      id={'admin.route.planning.edit.vehicles.max.travel.time.in.minutes'}
                      values={{ value: vehicle.maxTravelTimeInMinutes }}
                    />
                  </small>
                </div>
              ) : (
                ''
              )}
              {vehicle.maxLateTimeInMinutes ? (
                <div>
                  <small>
                    <FormattedMessage
                      id={'admin.route.planning.edit.vehicles.max.late.time.in.minutes'}
                      values={{ value: vehicle.maxLateTimeInMinutes }}
                    />
                  </small>
                </div>
              ) : (
                ''
              )}
              {vehicle.routingProfileName ? (
                <div>
                  <small>
                    <FormattedMessage
                      id={'admin.route.planning.edit.vehicles.routing.profile.name'}
                      values={{ value: vehicle.routingProfileName }}
                    />
                  </small>
                </div>
              ) : (
                ''
              )}
              <div>
                <button className="btn btn-normal-link" onClick={() => setEditVehicle(vehicle)}>
                  <FormattedMessage id={'admin.route.planning.edit.vehicles.edit'} />
                </button>
              </div>
            </div>
          );
        })}

        {!routePlan.vehicles.length ? (
          <p>
            <FormattedMessage id={'admin.route.planning.edit.vehicles.empty'} />
          </p>
        ) : (
          ''
        )}
      </div>

      <div className="m-b-6">{routes && routePlan ? <RoutesMap routes={routes} routePlan={routePlan} /> : ''}</div>

      <div className="m-b-6">
        <button
          onClick={() => {
            if (showOrderInfo) {
              setShowOrderInfo(false);
            } else {
              queryClient.invalidateQueries('routes');
            }
          }}
          className={`btn btn-small btn-primary m-r-2 ${routesLoading || routesFetching ? 'btn-loading' : ''}`}
          disabled={routesLoading || routesFetching}
        >
          <FormattedMessage id={'admin.route.planning.edit.actions.refresh'} />
        </button>
        <button
          onClick={() => {
            setShowOrderInfo(true);
          }}
          className={`btn btn-small btn-default m-r-2 ${routesLoading || routesFetching ? 'btn-loading' : ''}`}
          disabled={routesLoading || routesFetching}
        >
          <FormattedMessage id={'admin.route.planning.edit.actions.refresh.with.content'} />
        </button>
      </div>

      {routes ? (
        <>
          <div>
            <h4 className="m-b-3">
              <FormattedMessage
                id={'admin.route.planning.edit.deliveries.without.routes'}
                values={{ count: deliveriesWithoutRoute.length }}
              />
            </h4>
            <DeliveryTable
              showAddButton={true}
              selectedDeliveryDay={selectedDeliveryDay}
              routePlan={routePlan}
              routes={routes}
              deliveries={deliveriesWithoutRoute}
            />
          </div>

          <div className="row">
            {routes.map((route) => (
              <RouteTable
                key={route.id}
                route={route}
                selectedDeliveryDay={selectedDeliveryDay}
                routePlan={routePlan}
                routes={routes}
              />
            ))}
          </div>
        </>
      ) : (
        ''
      )}

      <div className="text-right">
        <button
          className={`btn btn-danger btn-small ${deleteLoading ? 'btn-loading' : ''}`}
          disabled={deleteLoading}
          onClick={() => {
            if (window.confirm(intl.formatMessage({ id: 'admin.route.planning.edit.actions.delete.confirm' }))) {
              deleteRoutePlan({ routePlanId: routePlan.id });
            }
          }}
        >
          <FormattedMessage id={'admin.route.planning.edit.actions.delete'} values={{ routePlanId: routePlan.id }} />
        </button>
      </div>

      <Modal show={!!editVehicle} onHide={() => setEditVehicle(null)} size="md">
        <div onClick={() => setEditVehicle(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {editVehicle ? (
            <VehicleForm
              vehicle={editVehicle}
              onSuccess={() => {
                setEditVehicle(null);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
      <Modal show={!!editRoute} onHide={() => setEditRoute(null)} size="md">
        <div onClick={() => setEditRoute(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {editRoute ? (
            <RouteForm
              route={editRoute}
              onSuccess={() => {
                setEditRoute(null);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
    </div>
  );
};

export default injectIntl(AdminRoutePlanEdit);
